import { useEffect, useState } from "react";
import useInView from "@/lib/useInView";

/**
 * AnimatedCounter — animates a numeric value from 0 → target when scrolled into view.
 * Renders the raw string when the value contains non-numeric chars (e.g. "End-to-End").
 * Props:
 *   value    – display value ("50+", "100+", "4", "End-to-End")
 *   duration – animation duration (ms)
 *   className
 */
export default function AnimatedCounter({ value, duration = 1600, className = "" }) {
  const [ref, inView] = useInView({ threshold: 0.4 });
  const [display, setDisplay] = useState(0);

  // Extract leading integer (if any) and suffix (e.g. "+", "%")
  const match = /^(-?\d+(?:\.\d+)?)(.*)$/.exec(String(value).trim());
  const numericTarget = match ? parseFloat(match[1]) : null;
  const suffix = match ? match[2] : "";

  useEffect(() => {
    if (!inView || numericTarget === null) return;
    const start = performance.now();
    let raf;

    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = numericTarget * eased;
      setDisplay(
        Number.isInteger(numericTarget) ? Math.round(current) : current.toFixed(1)
      );
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, numericTarget, duration]);

  if (numericTarget === null) {
    return (
      <span ref={ref} className={className}>
        {value}
      </span>
    );
  }

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
}
